import React, { useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Lock, Mail, ArrowLeft } from "lucide-react";
import Logo from "../components/Logo";

export default function Login() {
    const { login, user, loading } = useAuth();
    const navigate = useNavigate();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [error, setError] = useState("");
    const [submitting, setSubmitting] = useState(false);

    if (loading) return null;
    if (user) return <Navigate to={user.role === "client" ? "/espace-client" : "/dashboard"} replace />;

    const submit = async (e) => {
        e.preventDefault();
        setError("");
        setSubmitting(true);
        const res = await login(email.trim(), password);
        setSubmitting(false);
        if (res.ok) navigate(res.role === "client" ? "/espace-client" : "/dashboard");
        else setError(res.error || "Identifiants invalides");
    };

    return (
        <div data-testid="page-login" className="min-h-screen bg-ink-800 text-white flex flex-col">
            <div className="px-6 md:px-12 py-6">
                <Link to="/" data-testid="login-back-home" className="inline-flex items-center gap-2 text-white/60 hover:text-ice text-sm">
                    <ArrowLeft size={16} /> Retour au site
                </Link>
            </div>
            <div className="flex-1 flex items-center justify-center px-6 py-10">
                <div className="w-full max-w-md">
                    <div className="text-center mb-10">
                        <Logo variant="mark" className="h-24 w-auto mx-auto" />
                        <h1 className="font-serif text-4xl mt-8">Connexion</h1>
                        <p className="mt-4 text-base text-white/70 max-w-xs mx-auto">
                            Accédez à votre espace.
                        </p>
                    </div>

                    <form
                        data-testid="login-form"
                        onSubmit={submit}
                        className="bg-ink-600 border border-ice/10 rounded-xl p-8 space-y-5"
                    >
                        <label className="block">
                            <span className="text-xs tracking-[0.2em] text-white/60 uppercase">Email</span>
                            <div className="relative mt-2">
                                <Mail size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
                                <input
                                    data-testid="login-input-email"
                                    type="email"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    required
                                    autoComplete="email"
                                    className="w-full bg-ink-800 border border-white/15 text-white placeholder-white/40 focus:border-ice focus:ring-1 focus:ring-ice rounded-md pl-10 pr-4 py-3 outline-none text-sm"
                                />
                            </div>
                        </label>
                        <label className="block">
                            <span className="text-xs tracking-[0.2em] text-white/60 uppercase">Mot de passe</span>
                            <div className="relative mt-2">
                                <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
                                <input
                                    data-testid="login-input-password"
                                    type="password"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    required
                                    autoComplete="current-password"
                                    className="w-full bg-ink-800 border border-white/15 text-white placeholder-white/40 focus:border-ice focus:ring-1 focus:ring-ice rounded-md pl-10 pr-4 py-3 outline-none text-sm"
                                />
                            </div>
                        </label>

                        {error && (
                            <div data-testid="login-error" className="text-sm text-red-300 bg-red-500/10 border border-red-400/30 rounded-md px-4 py-3">
                                {error}
                            </div>
                        )}

                        <button
                            type="submit"
                            disabled={submitting}
                            data-testid="login-submit-button"
                            className="w-full bg-ice text-ink-900 hover:bg-white disabled:opacity-60 transition-colors px-7 py-3.5 rounded-md font-medium tracking-wide"
                        >
                            {submitting ? "Connexion…" : "Se connecter"}
                        </button>

                        <div className="pt-2 text-center">
                            <Link
                                to="/mot-de-passe-oublie"
                                data-testid="login-forgot-link"
                                className="text-sm text-white/60 hover:text-ice transition-colors"
                            >
                                Mot de passe oublié ?
                            </Link>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
}
